<template>
  <div class="timeline-event" :data-testid="`ticket-timeline-event-${event.kind}`">
    <div class="timeline-event__head">
      <WPills
        type="static"
        color="gray"
        :label="kindLabel"
        :section-tag="`ticket-timeline-event-kind-${event.kind}`"
      />
      <a
        v-if="event.url"
        :href="event.url"
        target="_blank"
        rel="noopener noreferrer"
        class="timeline-event__title"
      >
        {{ event.title }}
      </a>
      <span v-else class="timeline-event__title">{{ event.title }}</span>
      <WPills
        v-if="event.state"
        type="static"
        :color="stateColor"
        :label="stateLabel"
        :section-tag="`ticket-timeline-event-state-${event.state}`"
      />
      <span v-if="cost" class="timeline-event__meta">{{ cost }}</span>
      <span class="timeline-event__meta">{{ time }}</span>
      <FleetRunHandBackMenu
        v-if="isLiveRun && event.runId"
        :run-id="event.runId"
        :section-tag="`ticket-timeline-event-hand-back-${event.runId}`"
      />
    </div>
    <p v-if="event.detail" class="timeline-event__detail">{{ event.detail }}</p>
    <p v-if="event.runId" class="timeline-event__meta">Run {{ event.runId }}</p>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { isRunLive } from '@/services/claude-fleet'
import type { FleetRunState } from '@/services/claude-fleet'
import { timelineCost, timelineKindLabel, timelineStateColor } from '@/services/ticket-timeline'
import type { TimelineEvent } from '@/services/ticket-timeline'

import WPills from '@wasabi/actions/WPills.vue'

import FleetRunHandBackMenu from '@/views/claude-fleet/components/FleetRunHandBackMenu.vue'

import { formatDateTime } from '@/utils/dateUtils'

const props = defineProps<{ event: TimelineEvent }>()

const kindLabel = computed(() => timelineKindLabel(props.event.kind))

/** The fleet's raw state word ("MERGED", "ok") as a reader would say it. */
const stateLabel = computed(() => {
  const state = (props.event.state ?? '').replace(/[_-]+/g, ' ').trim().toLowerCase()
  if (state === 'ok') return 'Finished'
  return state.charAt(0).toUpperCase() + state.slice(1)
})

const stateColor = computed(() => timelineStateColor(props.event.state))

/** A run still going: one the reader may hand back from here. */
const isLiveRun = computed(() => isRunLive(props.event.state as FleetRunState | undefined))

const cost = computed(() => timelineCost(props.event))

const time = computed(() => (props.event.at ? formatDateTime(props.event.at) : ''))
</script>

<style scoped>
.timeline-event {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px 0;
  border-bottom: 1px solid rgb(var(--v-theme-neutral200));
}

.timeline-event__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.timeline-event__title {
  font-weight: 600;
  overflow-wrap: anywhere;
}

.timeline-event__detail {
  margin: 0;
  overflow-wrap: anywhere;
}

.timeline-event__meta {
  margin: 0;
  font-size: 12px;
  color: var(--color-content-text-muted, #767676);
}
</style>
